
import { DateTime, Interval } from "luxon";
import { Game } from "@/models/Game";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { cloneDeep, forIn, isEqual } from "lodash";
import DateHelper from "@/utils/DateHelper";
import { City } from "@/models/City";
import { Season } from "@/models/GameType";
import ImageBox from "@/components/Base/ImageBox.vue";
import ImageSelector from "@/components/Base/ImageSelector.vue";

@Component({
  components: { ImageBox, ImageSelector },
})
class ComposeGeneral extends Vue {
  @Prop({ default: null }) readonly gameData!: Game;
  @Prop({ default: false }) readonly isEditingLocked!: boolean;
  showSavePanel = false;
  min = new Date();
  endTime = "15:00";
  startTime = "08:00";
  successMessage = "";
  errorInstanceMessage = "";
  errorMessage: string[] = [];
  warningMessage: string[] = [];
  gameDataObject = {} as Game;
  isActive = false;
  getImage = "";
  showImageSelectorBool = false;
  showColor = false;

  get getCityData(): City {
    return this.$store.state.cityStore.singleCity;
  }

  get getImageString(): string {
    return this.getImage;
  }

  get getPreAppLaunch(): number {
    return this.$store.state.authenication.appPrelaunch._value;
  }

  get getErrorArray(): string[] {
    const arr: string[] = [];
    forIn(this.gameData.issues?.err, (val: any) => {
      arr.push(val.txt.en);
    });
    return arr;
  }
  get getSeasonData(): Season {
    return this.$store.state.gameStore.seasonData;
  }

  get getTimeZone(): string {
    if (this.getCityData.tZone === undefined) {
      return "Invalid";
    } else {
      return (
        this.getCityData.tZone.timeZoneId +
        ", " +
        this.getCityData.tZone.timeZoneName +
        " (" +
        DateTime.fromISO(this.gameData.start_dt, {
          zone: this.getCityData.tZone!.timeZoneId,
        }).offset /
          60 +
        ")"
      );
    }
  }
  get canEditDate(): boolean {
    var results = DateHelper.getStatus(this.gameData, this.getPreAppLaunch);
    if (results === "LIVE" || results === "PURGE" || results === "OPEN") {
      return true;
    }
    return false;
  }

  get getStartDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.gameData.start_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("dd/MM/yyyy") +
      " @ " +
      DateTime.fromISO(this.gameData.start_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("HH:mm")
    );
  }
  get getEndDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.gameData.end_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("dd/MM/yyyy") +
      " @ " +
      DateTime.fromISO(this.gameData.end_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("HH:mm")
    );
  }

  get getErrorMessage(): string {
    return this.errorInstanceMessage;
  }

  get geSuccessMessage(): string {
    return this.successMessage;
  }

  get getInstances(): string {
    if (
      this.gameData.evergreen !== undefined &&
      this.gameData.evergreen.cloneIDs !== undefined
    ) {
      let instanceString = [];
      for (const [key, value] of Object.entries(
        this.gameData.evergreen.cloneIDs
      )) {
        instanceString.push(key);
      }
      return instanceString.join(" ,");
    }
    return "No instances currently";
  }

  created(): void {
    this.gameDataObject = cloneDeep(this.gameData);

    this.endTime = DateTime.fromISO(this.gameData.end_dt).toFormat("HH:mm");
    this.startTime = DateTime.fromISO(this.gameData.start_dt).toFormat("HH:mm");
    if (this.gameData.corp === undefined) {
      this.clearImage();
      this.getImage = "test";
    } else {
      this.getImage = this.gameData.corp?.logoUrl || "";
    }
    if (this.gameData.notes === undefined) {
      this.gameData.notes = "";
    }
    if (this.gameData.evergreen === undefined) {
      this.gameData.evergreen = {
        isActive: false,
        isClone: false,
      };
      this.onValueChanged();
    }
    if (this.gameData.overrides === undefined) {
      this.gameData.overrides = {
        colours: {
          primary: "",
          secondary: "",
          tertiary: "",
        },
        gameTitleSuffix: { en: "" },
        mapboxUrl: "",
      };
    }
    this.isActive = this.gameData.evergreen?.isActive || false;
  }
  getColorCss(color: string): string {
    if (color === "") {
      return "background-color:#26c528";
    }
    return "background-color:" + color;
  }
  generateInstance(): void {
    this.$store
      .dispatch("toolBoxStore/processEverGreenGame", this.gameData.key)
      .then((result) => {
        this.errorInstanceMessage = "";
        this.successMessage = "";
        if (this.gameData.key != undefined) {
          if (result.data[this.gameData.key].length === 0) {
            this.successMessage = "No instances created";
          } else {
            this.successMessage =
              "Created the following instance: " +
              result.data[this.gameData.key];
          }
        }
      })
      .catch((error) => {
        this.errorInstanceMessage = "";
        this.successMessage = "";
        this.errorInstanceMessage = error;
      });
  }

  openColorPicker(): void {
    this.showColor = !this.showColor;
  }

  checkWarningDate(): void {
    this.warningMessage = [];
    this.gameData.start_dt = this.getUTCTime(
      this.gameData.start_dt,
      this.startTime
    );
    this.gameData.end_dt = this.getUTCTime(this.gameData.end_dt, this.endTime);
    var startDate = DateTime.fromISO(
      this.getUTCTime(this.gameData.start_dt, this.startTime)
    );
    var endDate = DateTime.fromISO(
      this.getUTCTime(this.gameData.end_dt, this.endTime)
    );
    var todayDate = new Date();
    const diff = Interval.fromDateTimes(todayDate, startDate);
    if (startDate > endDate) {
      this.warningMessage.push(
        "Please make sure the end date is in front of the start date"
      );
    }
    if (diff.length("hours") < 48) {
      this.warningMessage.push(
        "Date is less than 48 hours away. Please be careful as this data might be purged"
      );
    }
    this.onValueChanged();
  }
  getUTCTime(date: string, time: string): string {
    var time2 = DateTime.fromISO(time);
    return DateTime.fromISO(date.toString(), {
      zone: this.getCityData.tZone!.timeZoneId,
    })
      .set({
        hour: time2.hour,
        minute: time2.minute,
        second: 0,
        millisecond: 0,
      })
      .toISO();
  }
  onValueChanged(): void {
    this.errorMessage = [];
    if (this.gameData.admin_name.length < 3) {
      this.errorMessage.push(
        "Please make sure the admin name has enough characters"
      );
    } else if (this.checkDate()) {
      this.errorMessage.push(
        "Please make sure the end date is in front of the start date"
      );
    } else {
      if (!isEqual(this.gameData, this.gameDataObject)) {
        this.showSavePanel = true;
      } else {
        this.showSavePanel = false;
      }
      this.$emit("updateTempObject", this.gameData);
    }
  }
  checkDate(): boolean {
    if (
      DateTime.fromISO(
        this.getUTCTime(this.gameData.start_dt, this.startTime)
      ) < DateTime.fromISO(this.getUTCTime(this.gameData.end_dt, this.endTime))
    ) {
      return false;
    } else {
      return true;
    }
  }
  returnImagePath(path: string): void {
    this.getImage = this.getImagePath + "" + path;
    this.showImageSelectorBool = false;
    this.gameData.corp = { logoUrl: this.getImage };
    this.onValueChanged();
  }

  showImageSelector(): void {
    if (!this.isEditingLocked) {
      this.showImageSelectorBool = true;
    }
  }
  handleCloseButtonPress(): void {
    this.showImageSelectorBool = false;
  }

  clearImage(): void {
    if (!this.isEditingLocked) {
      this.getImage = "";
      this.gameData.corp = { logoUrl: this.getImage };
      this.onValueChanged();
    }
  }

  get getImagePath(): string {
    return "/corporate/logos/";
  }

  evergreenChanged(value: boolean): void {
    if (this.gameData.evergreen !== undefined) {
      this.gameData.evergreen.isActive = value;
    }
    this.onValueChanged();
  }
  @Watch("gameData")
  gameDataChanged(value: Game): void {
    if (this.gameData.evergreen === undefined && !this.isEditingLocked) {
      this.gameData.evergreen = {
        isActive: false,
        isClone: false,
      };
      this.onValueChanged();
    }
    this.isActive = this.gameData.evergreen?.isActive || false;
    if (this.gameData.overrides === undefined) {
      this.gameData.overrides = {
        colours: {
          primary: "",
          secondary: "",
          tertiary: "",
        },
        gameTitleSuffix: { en: "" },
        mapboxUrl: "",
      };
    }
    if (this.gameData.corp === undefined) {
      this.getImage = "test";
    } else {
      this.getImage = this.gameData.corp?.logoUrl || "";
    }
    this.successMessage = "";
    this.errorInstanceMessage = "";
    this.startTime = DateTime.fromISO(value.start_dt, {
      zone: this.getCityData.tZone!.timeZoneId,
    }).toFormat("HH:mm");
    this.endTime = DateTime.fromISO(value.end_dt, {
      zone: this.getCityData.tZone!.timeZoneId,
    }).toFormat("HH:mm");
  }
}

export default ComposeGeneral;
